/**
 * Redux slice for managing MAT (Multi-Academy Trust) related state
 * @module matSlice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { translate } from '../../../../utils/locale';
import { getExamLevelKey } from '../../../../types/enum';
import { getBenchmarkSetKey } from '../../../../utils/benchmarkHelpers';

/**
 * Initial state for the MAT slice
 * @type {MAT.State}
 */
const initialState: MAT.State = {
	selectedYear: undefined,
	loading: false,
	reportRequestPending: false,
	reportRequestError: '',
	clients: undefined,
	selectedClient: undefined,
	message: undefined,
	dataUploadsForChildren: undefined,
	groupsReports: [],
	editReportBundleError: undefined,
	showReportInfo: false,
	hasNewGroupReports: false,
	hasNewGroupReportsError: '',
};

/**
 * Redux slice containing reducers for MAT state management
 */
const matSlice = createSlice({
	name: 'mat',
	initialState,
	reducers: {
		/**
		 * Sets loading state when fetching MAT child data
		 * @param {MAT.State} state Current state
		 */
		setChildPending(state: MAT.State) {
			state.loading = true;
		},
		/**
		 * Handles failure when fetching MAT child data
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.Message>} action Action containing error message
		 */
		setChildFail(state: MAT.State, action: PayloadAction<MAT.Message>) {
			state.loading = false;
			state.message = action.payload;
		},
		/**
		 * Updates state with successfully fetched MAT clients data
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.Clients>} action Action containing clients data
		 */
		setChildSuccess(state: MAT.State, action: PayloadAction<MAT.Clients>) {
			state.loading = false;
			state.clients = action.payload;
		},
		/**
		 * Updates state with selected academic year
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.SelectedYear>} action Action containing selected year
		 */
		setSelectedYearSuccess(state: MAT.State, action: PayloadAction<MAT.SelectedYear>) {
			return {
				...state,
				loading: false,
				selectedYear: action.payload,
			};
		},
		/**
		 * Handles failure when setting selected year
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.Message>} action Action containing error message
		 */
		setSelectedYearFail(state: MAT.State, action: PayloadAction<MAT.Message>) {
			state.loading = false;
			state.message = action.payload;
		},
		/**
		 * Updates state with selected MAT client
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.SelectedClient>} action Action containing selected client
		 */
		setSelectedClient(state: MAT.State, action: PayloadAction<MAT.SelectedClient>) {
			state.loading = false;
			state.selectedClient = action.payload;
		},
		/**
		 * Updates selected year for a specific client
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<string>} action Action containing year value
		 */
		setClientSelectedYear(state: MAT.State, action: PayloadAction<string>) {
			state.loading = false;
			if (state.selectedClient) state.selectedClient.selectedYear = action.payload;
		},
		/**
		 * Clears the selected client from state
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<undefined>} action Action with undefined payload
		 */
		unsetSelectedClient(state: MAT.State, action: PayloadAction<undefined>) {
			state.loading = false;
			state.selectedClient = action.payload;
		},
		/**
		 * Updates state with data uploads for child schools
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.DataUploads>} action Action containing data uploads
		 */
		setDataUploadsSuccess(state: MAT.State, action: PayloadAction<MAT.DataUploads>) {
			state.loading = false;
			state.dataUploadsForChildren = action.payload;
		},
		/**
		 * Handles failure when fetching data uploads
		 * @param {MAT.State} state Current state
		 */
		setDataUploadsFail(state: MAT.State) {
			state.loading = false;
		},
		/**
		 * Sets loading state when fetching groups reports by group access id
		 * @param {MAT.State} state Current state
		 */
		setGroupsReportsByGroupAccessIdPending(state: MAT.State) {
			return {
				...state,
				loading: true,
			};
		},
		/**
		 * Updates state with groups reports by group access id
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<ReportBundleState[]>} action Action containing report bundles
		 */
		setGroupsReportsByGroupAccessIdSuccess(
			state: MAT.State,
			action: PayloadAction<MAT.ReportBundle[]>
		) {
			const reports: MAT.ReportBundle[] = action.payload.map((report) => {
				return {
					...report,
					isLatest: report.isLatest,
					label:
						report.label ??
						translate('reports.groupReports.data.DEFAULT_LABEL', {
							examLevels: getExamLevelKey(report.examLevels),
							benchmarkSetType: getBenchmarkSetKey(report.benchmarkSetTypeId),
						}),
				};
			});

			return {
				...state,
				loading: false,
				groupsReports: reports, //action.payload
			};
		},
		/**
		 * Handles failure when fetching groups reports
		 * @param {MAT.State} state Current state
		 */
		setGroupsReportsByGroupAccessIdFailed(state: MAT.State) {
			return {
				...state,
				loading: false,
			};
		},
		/**
		 * Sets loading state when editing report bundle label and bookmarked status
		 * @param {MAT.State} state Current state
		 */
		setEditReportBundleLabelAndBookmarkedStatusPending(state: MAT.State) {
			return {
				...state,
				loading: true,
			};
		},
		/**
		 * Handles failure when editing report bundle label and bookmarked status
		 * @param {MAT.State} state Current state
		 */
		setEditReportBundleLabelAndBookmarkedStatusFailed(
			state: MAT.State,
			action: PayloadAction<MAT.Message>
		) {
			return {
				...state,
				loading: false,
				editReportBundleError: action.payload,
			};
		},
		/**
		 * Sets loading state when generating report request
		 * @param {MAT.State} state Current state
		 */
		setGenerateReportRequestPending(state: MAT.State, action: PayloadAction<boolean>) {
			return {
				...state,
				reportRequestPending: action.payload,
			};
		},
		/**
		 * Handles failure when generating report request
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.Message>} action Action containing error message
		 */
		setGenerateReportRequestFailed(state: MAT.State, action: PayloadAction<MAT.Message>) {
			const { text } = action.payload;
			return {
				...state,
				reportRequestPending: false,
				reportRequestError: text,
			};
		},
		/**
		 * Sets success state when generating report request
		 * @param {MAT.State} state Current state
		 * @param {PayloadAction<MAT.ReportRequestPendingStatus>} action Action containing report request pending status
		 */
		setGenerateReportRequestSuccess(state: MAT.State) {
			return {
				...state,
				reportRequestError: '',
			};
		},
		setHasNewGroupReports(state: MAT.State, action: PayloadAction<boolean>) {
			return {
				...state,
				hasNewGroupReports: action.payload,
			};
		},
		setHasNewGroupReportsError(state: MAT.State) {
			return {
				...state,
				hasNewGroupReportsError:
					'An error occurred trying to retrieve new reports. Please try again later.',
				hasNewGroupReports: false, // we can't determine that there are any new group reports
			};
		},
		setShowReportInfo(state: MAT.State, action: PayloadAction<boolean>) {
			return {
				...state,
				showReportInfo: action.payload,
			};
		},
		setReportAccordionLoading(state: MAT.State, action: PayloadAction<boolean>) {
			return {
				...state,
				loading: action.payload,
			};
		},
	},
});

/**
 * Action creators for MAT slice
 */
export const {
	setChildFail,
	setChildPending,
	setChildSuccess,
	setSelectedYearSuccess,
	setSelectedYearFail,
	setSelectedClient,
	setClientSelectedYear,
	unsetSelectedClient,
	setDataUploadsSuccess,
	setDataUploadsFail,
	setGroupsReportsByGroupAccessIdPending,
	setGroupsReportsByGroupAccessIdSuccess,
	setGroupsReportsByGroupAccessIdFailed,
	setEditReportBundleLabelAndBookmarkedStatusPending,
	setEditReportBundleLabelAndBookmarkedStatusFailed,
	setGenerateReportRequestPending,
	setGenerateReportRequestFailed,
	setGenerateReportRequestSuccess,
	setHasNewGroupReports,
	setHasNewGroupReportsError,
	setShowReportInfo,
	setReportAccordionLoading,
} = matSlice.actions;

/**
 * MAT slice reducer
 */
export default matSlice.reducer;
